import {
  authErrorResponseSchema,
  authOAuthInitResponseSchema,
  authOkResponseSchema,
  authSessionResponseSchema,
  authUserResponseSchema,
  type User,
} from '@w/auth'
import {
  createOperationTransport,
  type FetchFn,
  type OnlineDetector,
  type RetryFetchOptions,
} from '@w/utils'
import { storageChangeEvent, useLocalStorage } from '@w/utils-fe'
import type { Schema } from '@w/schema'
import { ulid } from '@w/utils'
import { serializeBaggage, type BoundaryTelemetry, type Tel } from '@w/tel'

export type { User }

export const AUTH_USER_KEY = 'auth:user'
/** flashcards-local-first.spec.md Terms: the device's current anonymous user, minted on this device and never sent to the server. */
export const AUTH_ANONYMOUS_KEY = 'auth:anonymous'
/** The Web Lock every tab of a profile takes to mint the anonymous user (one per profile). */
export const AUTH_ANONYMOUS_MINT_LOCK = 'auth-anonymous-mint'

/** A principal minted on the device (`anonymous: true`); an account user never carries the flag. */
export type Principal<AuthUser = User> = AuthUser & { anonymous?: true }

/** True when an account user is stored; the anonymous principal is not a login. */
export function useAuthIsLoggedIn(): boolean {
  const [user] = useLocalStorage<User | null>(AUTH_USER_KEY, null)
  return user !== null
}

function anonymousUser(id: string): User & { anonymous: true } {
  return { id, anonymous: true, email: '', emailVerified: false, name: null, avatar: null, role: 'user', providers: [], country: null, state: null, settings: {}, createdAt: new Date(), lastSigninAt: null }
}

interface ResponseSchema<T> {
  parse(data: unknown): T
}

export interface AuthClientConfig {
  tel: Tel
  onSession?: (user: User | null) => void
  baseUrl?: string
  fetch?: FetchFn
  retry?: RetryFetchOptions | false
  onlineDetector?: OnlineDetector
  storage?: Storage
  /** Dev/test adapter for users not backed by the auth DB. */
  updateSettings?: (settings: Record<string, unknown>) => Promise<User>
  /** flashcards-local-first.spec.md: with no account stored, the principal is the device's anonymous user. */
  anonymous?: boolean
}

export type UserWithSettings<Settings extends Record<string, unknown>> = Omit<
  User,
  'settings'
> & {
  settings: Settings
}

export interface AuthClient<AuthUser = User> {
  signIn: {
    magicLink: (opts: { email: string }) => Promise<void>
    social: (opts: {
      provider: 'google' | 'telegram'
      redirectUri: string
    }) => Promise<void>
    googleIdToken: (opts: { idToken: string }) => Promise<AuthUser>
  }
  callback: {
    magicLink: (opts: { token: string }) => Promise<AuthUser>
    social: (opts: {
      provider: 'google' | 'telegram'
      code: string
      state: string
    }) => Promise<AuthUser>
  }
  /** `beforeLocalSignOut` runs before the stored user is removed, so views that read the user can leave first; when supplied, remote revocation completes before this teardown callback. */
  signOut: (beforeLocalSignOut?: () => Promise<void>) => Promise<void>
  refreshUser: (tel: Tel) => Promise<AuthUser | null>
  /** Mints the device's anonymous user once (one per browser profile, under a Web Lock) when no principal is stored. */
  ensurePrincipal: () => Promise<void>
  updateSettings: (
    settings: AuthUser extends UserWithSettings<infer S> ? S : never,
  ) => Promise<AuthUser>
  useUser: () => AuthUser
  useIsLoggedIn: () => boolean
  getUser: () => AuthUser
  getUserOrNull: () => AuthUser | null
  /** True when an account user is stored; false for the anonymous principal. */
  isLoggedIn: () => boolean
  /** True only after this client authenticated the current account with the server. */
  hasFreshSession: () => boolean
}

export interface RequireUserOptions<T, AuthUser = User> {
  ctx: { auth: AuthClient<AuthUser> }
  redirect: (opts: { to: string }) => T
  loginPath?: string
  tel: Tel
}

export interface EnsureGetUserInCtxOptions<T, AuthUser = User> {
  ctx: { auth: AuthClient<AuthUser>; getUser: () => AuthUser }
  redirect: (opts: { to: string }) => T
  loginPath?: string
  tel: Tel
}

export interface HandleMagicLinkCallbackOptions<T, AuthUser = User> {
  ctx: { auth: AuthClient<AuthUser> }
  redirect: (opts: { to: string }) => T
  token: string | undefined
  successPath: string
  loginPath?: string
}

/**
 * Route guard over the stored identity; it sends no request. The server validates the session on
 * every request that needs it (auth request ctx), and the app signs out on that request's 401.
 */
export function _requireAuthUser<T, AuthUser>({
  ctx,
  redirect,
  loginPath = '/login',
}: RequireUserOptions<T, AuthUser>): Promise<AuthUser> {
  const user = ctx.auth.getUserOrNull()
  // oxlint-disable-next-line typescript/only-throw-error -- redirect() is router control flow.
  if (!user) throw redirect({ to: loginPath })
  return Promise.resolve(user)
}

export async function _ensureGetUserInCtx<T, AuthUser>({
  ctx,
  redirect,
  loginPath,
  tel,
}: EnsureGetUserInCtxOptions<T, AuthUser>): Promise<AuthUser> {
  const user = await _requireAuthUser({ ctx, redirect, loginPath, tel })
  ctx.getUser = ctx.auth.getUser
  return user
}

export async function _handleAuthMagicLinkCallback<T, AuthUser>({
  ctx,
  redirect,
  token,
  successPath,
  loginPath = '/login',
}: HandleMagicLinkCallbackOptions<T, AuthUser>): Promise<never> {
  if (!token) {
    // oxlint-disable-next-line typescript/only-throw-error -- redirect() is router control flow.
    throw redirect({ to: loginPath })
  }

  try {
    await ctx.auth.callback.magicLink({ token })
    // oxlint-disable-next-line typescript/only-throw-error -- redirect() is router control flow.
    throw redirect({ to: successPath })
  } catch (error) {
    if (error && typeof error === 'object' && 'options' in error) {
      throw error
    }
    // oxlint-disable-next-line typescript/only-throw-error -- redirect() is router control flow.
    throw redirect({ to: loginPath })
  }
}

// Blocked site data makes the window.localStorage getter throw a SecurityError.
function browserLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function createAuthClient(config: AuthClientConfig): AuthClient
export function createAuthClient<Settings extends Record<string, unknown>>(
  config: AuthClientConfig & { userSettingsSchema: Schema<Settings> },
): AuthClient<UserWithSettings<Settings>>
export function createAuthClient<Settings extends Record<string, unknown>>(
  config: AuthClientConfig & { userSettingsSchema?: Schema<Settings> },
): AuthClient {
  const baseUrl = (config.baseUrl ?? '').replace(/\/$/, '')
  const authUrl = `${baseUrl}/auth`
  const resourceTel = config.tel
  const storage = config.storage ?? browserLocalStorage()
  let settingsWrites: Promise<unknown> = Promise.resolve()
  let pendingSettingsWrites = 0
  let confirmedSettingsUser: User | null = null
  let sessionUserId: string | undefined
  const transport = createOperationTransport<
    { path: string; init?: RequestInit },
    Response
  >({
    fetch: config.fetch ?? fetch,
    retry: config.retry ?? false,
    onlineDetector: config.onlineDetector,
    execute: ({ path, init }, ctx) =>
      ctx.fetch(`${authUrl}${path}`, withAuthDefaults(init)),
  })

  async function request<T>(
    path: string,
    schema: ResponseSchema<T>,
    opts: RequestInit,
    tel: Tel,
  ): Promise<T> {
    let result!: T
    // Never record query tokens, request bodies, or response credentials.
    await tel.span({
      name: `auth${path.split('?')[0]}`,
      target: 'fetch',
      input: undefined,
    }, async spanTel => {
      const headers = new Headers(opts.headers)
      for (const [name, value] of Object.entries(serializeBaggage(spanTel._getBaggage(), { mode: 'native' }))) {
        headers.set(name, value)
      }
      const res = await transport.execute({ path, init: { ...opts, headers } })
      const data = await res.json() as { telemetry?: BoundaryTelemetry }
      if (data.telemetry) {
        spanTel._insertBoundaryTrace(data.telemetry.trace, data.telemetry.metrics, data.telemetry.resource)
      }
      if (!res.ok) {
        const error = authErrorResponseSchema.safeParse(data)
        throw new Error(error.success ? error.data.error : 'Request failed')
      }
      result = schema.parse(data)
    })
    return result
  }

  type ClientUser = User | UserWithSettings<Settings>

  function parseUser(user: User): ClientUser {
    if (!config.userSettingsSchema) return user
    const { settings, ...rest } = user
    return {
      ...rest,
      settings: config.userSettingsSchema.parse(settings),
    }
  }

  function saveUser(user: User): ClientUser {
    const parsed = parseUser(user)
    if (!storage) return parsed
    const value = JSON.stringify(parsed)
    try {
      storage.setItem(AUTH_USER_KEY, value)
    } catch {
      // Blocked storage: the session cookie stays the source of truth.
      return parsed
    }
    storageChangeEvent.emit({ key: AUTH_USER_KEY, newValue: value, storage })
    return parsed
  }

  function readStored(key: string): User | null {
    if (!storage) return null
    try {
      // Blocked site data makes getItem throw a SecurityError: read as absent.
      const item = storage.getItem(key)
      if (!item) return null
      const stored = JSON.parse(item) as User
      return config.userSettingsSchema ? parseUser(stored) : stored
    } catch {
      return null
    }
  }

  /** The one principal source: the stored account, else (when enabled) the device's anonymous user. */
  function principal(account: User | null, anonymous: User | null) {
    return account ?? (config.anonymous ? anonymous : null)
  }

  async function ensurePrincipal() {
    if (!config.anonymous || !storage || getUserOrNull()) return
    const mint = () => {
      if (getUserOrNull()) return
      const value = JSON.stringify(anonymousUser(ulid()))
      try { storage.setItem(AUTH_ANONYMOUS_KEY, value) } catch { return }
      storageChangeEvent.emit({ key: AUTH_ANONYMOUS_KEY, newValue: value, storage })
    }
    // Two tabs opening for the first time mint one user: the lock holder re-reads before it writes.
    // No lock manager (happy-dom; a browser without Web Locks): a documented limit, not a guarantee. The check and the
    // write run in one synchronous task, but two tabs can still both mint (flashcards-local-first.spec.md D12).
    const locks = typeof navigator === 'undefined' ? undefined : (navigator.locks as LockManager | null | undefined)
    if (locks) await locks.request(AUTH_ANONYMOUS_MINT_LOCK, mint)
    else mint()
  }

  function removeUser() {
    sessionUserId = undefined
    if (!storage) return
    try {
      storage.removeItem(AUTH_USER_KEY)
    } catch {
      return
    }
    storageChangeEvent.emit({ key: AUTH_USER_KEY, newValue: null, storage })
  }

  function getUserOrNull() {
    const account = readStored(AUTH_USER_KEY)
    return principal(account, account ? null : readStored(AUTH_ANONYMOUS_KEY))
  }

  function getUser() {
    const user = getUserOrNull()
    if (!user) throw new Error('Not authenticated')
    return user
  }

  function saveSessionUser(user: User) {
    sessionUserId = user.id
    config.onSession?.(user)
    return saveUser(user)
  }

  async function refreshUser(tel: Tel) {
    sessionUserId = undefined
    let data
    try {
      data = await request('/session', authSessionResponseSchema, {
        method: 'GET',
      }, tel)
    } catch (error) {
      // Keep the cached identity for offline pages; it does not admit network work.
      tel.warn('Session check unavailable; keeping the stored user.', { error })
      config.onSession?.(null)
      return null
    }
    if (data.ok && data.user) {
      return saveSessionUser(data.user)
    }
    config.onSession?.(null)
    removeUser()
    return null
  }

  async function verifyMagicLink(token: string) {
    // Server decides whether this token authenticates an account.
    const data = await request(
      `/magic-link/verify?token=${token}`,
      authUserResponseSchema,
      {
        method: 'GET',
      },
      resourceTel,
    )
    return saveSessionUser(data.user)
  }

  return {
    signIn: {
      async magicLink({ email }) {
        await request('/magic-link/send', authOkResponseSchema, {
          method: 'POST',
          body: JSON.stringify({ email }),
        }, resourceTel)
      },

      async social({ provider, redirectUri }) {
        // Server supplies the OAuth authorization URL.
        const data = await request(
          `/${provider}/init`,
          authOAuthInitResponseSchema,
          {
            method: 'POST',
            body: JSON.stringify({ redirectUri }),
          },
          resourceTel,
        )
        window.location.href = data.authUrl
      },

      async googleIdToken({ idToken }) {
        // Server verifies the credential before establishing a session.
        const data = await request('/google/id-token', authUserResponseSchema, {
          method: 'POST',
          body: JSON.stringify({ idToken }),
        }, resourceTel)
        return saveSessionUser(data.user)
      },
    },

    callback: {
      magicLink({ token }) {
        return verifyMagicLink(token)
      },

      async social({ provider, code, state }) {
        // Server verifies the OAuth callback before establishing a session.
        const data = await request(
          `/${provider}/callback`,
          authUserResponseSchema,
          {
            method: 'POST',
            body: JSON.stringify({ code, state }),
          },
          resourceTel,
        )
        return saveSessionUser(data.user)
      },
    },

    async signOut(beforeLocalSignOut) {
      // Callers drain application writes before signOut; keep their resources alive until revocation settles.
      const revoke = request('/logout', authOkResponseSchema, { method: 'POST' }, resourceTel)
        .catch(error => { resourceTel.warn('Remote logout unavailable; signing out locally.', { error }) })
      if (beforeLocalSignOut) await revoke
      try {
        if (beforeLocalSignOut) await beforeLocalSignOut()
      } finally {
        removeUser()
      }
      await revoke
    },

    refreshUser,

    ensurePrincipal,

    async updateSettings(settings) {
      const current: Principal | null = getUserOrNull()
      if (current?.anonymous) {
        // flashcards-local-first.spec.md: anonymous settings stay on the device.
        const value = JSON.stringify({ ...current, settings })
        storage?.setItem(AUTH_ANONYMOUS_KEY, value)
        if (storage) storageChangeEvent.emit({ key: AUTH_ANONYMOUS_KEY, newValue: value, storage })
        return parseUser({ ...current, settings })
      }
      if (!current) throw new Error('Not authenticated')
      if (pendingSettingsWrites === 0) confirmedSettingsUser = current
      pendingSettingsWrites++
      saveUser({ ...current, settings })
      const write = settingsWrites.then(async () => {
        try {
          const user = config.updateSettings
            ? await config.updateSettings(settings)
            : (await request('/settings', authUserResponseSchema, {
              method: 'PUT',
              body: JSON.stringify(settings),
            }, resourceTel)).user
          confirmedSettingsUser = user
          return parseUser(user)
        } finally {
          pendingSettingsWrites--
          if (pendingSettingsWrites === 0 && confirmedSettingsUser && getUserOrNull()?.id === current.id) {
            saveUser(confirmedSettingsUser)
          }
        }
      })
      // Serialize persistence so a slower earlier write cannot replace the last click.
      // Rejections still reach the caller's existing error path.
      settingsWrites = write.catch(() => null)
      const updated = await write
      return updated
    },

    useUser() {
      const [account] = useLocalStorage<ClientUser | null>(AUTH_USER_KEY, null)
      const [anonymous] = useLocalStorage<ClientUser | null>(AUTH_ANONYMOUS_KEY, null)
      const user = principal(account, anonymous)
      if (!user) throw new Error('Not authenticated')
      return user
    },

    useIsLoggedIn() {
      return useAuthIsLoggedIn()
    },

    getUser,

    getUserOrNull,

    isLoggedIn: () => readStored(AUTH_USER_KEY) !== null,
    hasFreshSession: () => sessionUserId !== undefined && sessionUserId === readStored(AUTH_USER_KEY)?.id,
  }
}

function withAuthDefaults(init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return {
    ...init,
    headers,
    credentials: 'include',
  }
}
